import * as React from 'react';
import { FC } from 'react';

import { useRouter } from 'expo-router';

import DrawerPage from 'components/layout/drawer-page';
import { useRoute } from 'hooks/navigation/use-route';
import { useSupportData } from 'hooks/use-support-data';
import { appendObjectToQueryString } from 'navigation/utils';
import { t } from 'state/intl/translations';
import { routes } from 'utils/routing';

import { ErrorView } from '../error-view';
import { LoadingView } from '../loading-view';
import { SubcategoryBreadcrumbs } from '../subcategory-breadcrumbs';
import { ISupportPageV2Props } from '../types';

import { SupportV2CategoryView } from './support-v2-category-view';
import { ISubcategoryContent } from './types';

/**
 * Displays list of subcategories which link to the support form, or orders list, or customer initiated refund pages
 */
const SupportV2CategoryPage: FC<React.PropsWithChildren<unknown>> = () => {
  const { navigate } = useRouter();
  const { params } = useRoute<ISupportPageV2Props>();
  const { categoryId, orderId } = params;

  const { supportCategoryGroups, isSupportDataLoading } = useSupportData();

  if (isSupportDataLoading) {
    return <LoadingView />;
  }

  const categoryGroup = supportCategoryGroups?.find(group => group && group._id === categoryId);

  const categoryName = categoryGroup?.name?.locale ?? '';

  if (!supportCategoryGroups || !categoryGroup) {
    return <ErrorView message={t('looksLikeWereExperiencingIssues')} />;
  }

  const pageTitleText = t('supportSubcategoryTitle', {
    category: categoryName,
  });

  const subcategories =
    categoryGroup.supportSubcategories?.map(subcat => ({
      _id: subcat?._id ?? '',
      name: subcat?.name?.locale ?? '',
      supportSubcategoryForwardToOrders: subcat?.supportSubcategoryForwardToOrders || false,
    })) || [];

  const breadcrumbSectionName = categoryGroup.name?.locale || '';

  const onSelectSubcategory = (subcategory: ISubcategoryContent) => {
    const routeParams = {
      categoryId,
      subcategoryId: subcategory._id,
      ...(orderId && { orderId }),
    };

    if (subcategory.supportSubcategoryForwardToOrders) {
      return navigate(appendObjectToQueryString(`${routes.supportOrders}`, routeParams));
    }

    return navigate(appendObjectToQueryString(`${routes.supportForm}`, routeParams));
  };

  return (
    <DrawerPage headerText={pageTitleText} dismissType="back">
      <SubcategoryBreadcrumbs currentSectionName={breadcrumbSectionName} />
      <SupportV2CategoryView
        pageTitle={pageTitleText}
        subcategories={subcategories}
        onSelectSubcategory={onSelectSubcategory}
      />
    </DrawerPage>
  );
};

export default SupportV2CategoryPage;
